import React, { useEffect, useState } from 'react';
import { StepType } from '@reactour/tour';
import { NextRouter } from 'next/router';
import { Box, Typography, Button, Skeleton, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useSettings, Iconify } from '@sdf/base';
import { getTourCompletedVersion, getTourStorageKey, getTourUserEmail } from '../utils/tourWelcome';
import { BONUS_WINDOW_MS, getOrCreateBonusAnchorMs } from '../utils/bonusCountdown';

const AGENT_UPSELL_STATS = [
  { value: '20', label: 'Revenue Ready Videos', sublabel: 'launched from one prompt' },
  { value: '50', label: 'UGC Clips', sublabel: 'generated from one click' },
  { value: '100+', label: 'Photo Ad Variations', sublabel: 'generated instantaneously' },
] as const;

const AGENT_UPSELL_FEATURES = [
  'Make images and videos in bulk instead of generating one by one',
  'Keep creating while batch jobs finish in the background',
  'Significant reduction in weekly content production cycles',
  'Priority agent queue plus support',
  'Top up extra credits for $5 anytime',
  'Connect Claude, Codex, or any MCP-compatible AI agent using our agent MCP',
] as const;

const getRemainingTime = (
  hasActivePlan: boolean,
  hadPreviousActiveSubscription: boolean,
  bonusAnchorMs: number
): string => {
  if (hasActivePlan || hadPreviousActiveSubscription) return '0:00';

  const elapsed = Date.now() - bonusAnchorMs;
  const remaining = Math.max(0, BONUS_WINDOW_MS - elapsed);
  const minutes = Math.floor(remaining / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);

  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

const useOfferCountdown = (
  hasActivePlan: boolean,
  hadPreviousActiveSubscription: boolean
): string => {
  const [bonusAnchorMs, setBonusAnchorMs] = useState<number>(() =>
    getOrCreateBonusAnchorMs({ resetOnPageLoad: true })
  );
  const [timeLeft, setTimeLeft] = useState(() =>
    getRemainingTime(hasActivePlan, hadPreviousActiveSubscription, bonusAnchorMs)
  );

  useEffect(() => {
    if (hasActivePlan || hadPreviousActiveSubscription) return;
    setBonusAnchorMs(getOrCreateBonusAnchorMs({ resetOnPageLoad: true }));
  }, [hasActivePlan, hadPreviousActiveSubscription]);

  useEffect(() => {
    setTimeLeft(getRemainingTime(hasActivePlan, hadPreviousActiveSubscription, bonusAnchorMs));

    if (hasActivePlan || hadPreviousActiveSubscription) {
      return;
    }

    const timer = window.setInterval(() => {
      setTimeLeft(getRemainingTime(hasActivePlan, hadPreviousActiveSubscription, bonusAnchorMs));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [hasActivePlan, hadPreviousActiveSubscription, bonusAnchorMs]);

  return timeLeft;
};

const getSteps = (
  router: NextRouter,
  userPlanData: string,
  agentPlanData: string,
  enableAgentPlan: boolean,
  userData: any,
  nextPlanData: any,
  onTourSyncChange: (isSyncing: boolean) => void,
  hadPreviousActiveSubscription: boolean
): StepType[] => {
  const { onChangeWelcomeTour } = useSettings();
  const tourUserEmail = getTourUserEmail(userData);
  const tourStepKey = getTourStorageKey('tourStep', tourUserEmail);
  const tourInitializedKey = getTourStorageKey('tourInitialized', tourUserEmail);
  type PanelSize = 'small' | 'medium' | 'large';
  const theme = useTheme();
  const graySurface = alpha(theme.palette.grey[900], 0.96);
  const graySurfaceElevated = alpha(theme.palette.grey[800], 0.92);
  const graySurfaceMuted = alpha(theme.palette.grey[100], 0.05);
  const graySurfaceSoft = alpha(theme.palette.grey[100], 0.08);
  const grayBorder = alpha(theme.palette.grey[100], 0.12);
  const grayBorderSoft = alpha(theme.palette.grey[100], 0.08);
  const grayBorderHover = alpha(theme.palette.grey[100], 0.18);
  const grayTextMuted = alpha(theme.palette.grey[100], 0.82);
  const grayTextSubtle = alpha(theme.palette.grey[100], 0.52);
  const grayTextDim = alpha(theme.palette.grey[100], 0.34);
  const grayTextFaint = alpha(theme.palette.grey[100], 0.22);
  const grayShadow = alpha(theme.palette.grey[900], 0.38);
  const compactViewportQuery = '@media (max-width:420px), (max-height:810px)';
  const showPaidColumn = agentPlanData !== 'agentTrialPlan';
  const showAgentColumn =
    ((userPlanData === 'activePlan' && agentPlanData !== 'agentPlan') ||
      agentPlanData === 'agentTrialPlan') &&
    enableAgentPlan;
  const showStandardComparisonRows = userPlanData !== 'activePlan' && agentPlanData !== 'agentPlan';
  const hasActivePlan = userPlanData === 'activePlan';
  const isPaidMainWithoutAgent =
    enableAgentPlan && userPlanData === 'activePlan' && agentPlanData !== 'agentPlan';
  const firstStepValueColumnCount = Number(showPaidColumn) + Number(showAgentColumn);
  const firstStepNameColumnWidth = firstStepValueColumnCount === 2 ? '40%' : '36%';
  const firstStepValueColumnWidth = firstStepValueColumnCount === 2 ? '30%' : '64%';
  const monthlyAgentAmount =
    typeof nextPlanData?.agentAddonAmount === 'number' ? nextPlanData.agentAddonAmount : null;
  const isYearlyMain = nextPlanData?.interval === 'Yearly';
  const billingSuffix = isYearlyMain ? 'yr' : 'mo';
  const displayAgentAmount = monthlyAgentAmount;
  const trialUser = userPlanData === 'freeTrialAccess' || agentPlanData === 'agentTrialPlan';
  const noPlanUser = userPlanData === 'noActivePlan' && agentPlanData === 'noAgentPlan';

  const renderAmountSkeleton = (width = 88) => (
    <Skeleton
      variant="rounded"
      width={width}
      height={22}
      sx={{
        display: 'inline-block',
        bgcolor: (t) => alpha(t.palette.grey[100], 0.2),
      }}
    />
  );

  const TourPanel: React.FC<{
    children?: React.ReactNode;
    size?: PanelSize;
  }> = ({ children, size = 'medium' }) => {
    const widths: Record<PanelSize, string> = {
      small: '360px',
      medium: '440px',
      large: '860px',
    };

    return (
      <Box
        sx={{
          width: { xs: ['min(352px, 90vw)'], sm: ['min(' + widths[size] + ', 92vw)'] },
          maxWidth: '92vw',
          p: { xs: 0.35, sm: 1.25 },
          pb: 0,
          borderRadius: 2,
          textAlign: 'center',
          bgcolor: 'transparent',
        }}
      >
        <Box
          sx={{
            p: { xs: 0.65, sm: 1.25 },
            color: theme.palette.text.primary,
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 0.5, sm: 1.1 },
            [compactViewportQuery]: {
              p: 0.55,
              gap: 0.45,
            },
          }}
        >
          {children}
        </Box>
      </Box>
    );
  };

  const PanelHeader: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <Typography
      variant="h6"
      sx={{
        fontWeight: 600,
        fontSize: { xs: '1rem', sm: '1.25rem' },
        lineHeight: 1.2,
        textAlign: 'center',
        color: theme.palette.text.primary,
        mb: 0.25,
      }}
    >
      {children}
    </Typography>
  );

  const PanelBody: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <Typography
      sx={{
        color: theme.palette.text.primary,
        fontSize: { xs: '0.9rem', sm: '1rem' },
        textAlign: 'center',
      }}
    >
      {children}
    </Typography>
  );

  const PanelHighlight: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
    return (
      <Box
        sx={{
          mt: 1,
          p: { xs: 0.75, sm: 1.25 },
          borderRadius: 1,
          background: theme.palette.background.ternaryV2,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: { xs: '0.95rem', sm: '1rem' },
            textAlign: 'center',
          }}
        >
          {children}
        </Typography>
      </Box>
    );
  };

  const TimeBadge: React.FC<{ value: string; label: string }> = ({ value, label }) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: graySurfaceSoft,
        border: `0.5px solid ${grayBorderSoft}`,
        borderRadius: '10px',
        px: { xs: 1.25, sm: 1.75 },
        py: { xs: 0.85, sm: 1 },
        minWidth: { xs: 48, sm: 54 },
      }}
    >
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 700,
          color: theme.palette.grey[100],
          lineHeight: 1,
        }}
      >
        {value}
      </Typography>
      <Typography sx={{ fontSize: '12px', color: grayTextSubtle, mt: 0.35 }}>{label}</Typography>
    </Box>
  );

  const FeatureTableRow: React.FC<{
    name: React.ReactNode;
    trialValue?: React.ReactNode;
    paidValue: React.ReactNode;
    agentValue: React.ReactNode;
    locked?: boolean;
    includeTrialColumn?: boolean;
  }> = ({ name, trialValue, paidValue, agentValue, locked, includeTrialColumn = true }) => (
    <tr>
      <td>{name}</td>
      {includeTrialColumn && trialUser && (
        <td
          style={{
            color: locked ? grayTextFaint : theme.palette.grey[100],
            fontWeight: 600,
            textAlign: 'left',
          }}
        >
          {trialValue}
        </td>
      )}
      {showPaidColumn && paidValue && (
        <td
          style={{
            color: theme.palette.grey[100],
            fontWeight: 600,
            textAlign: 'left',
          }}
        >
          {paidValue}
        </td>
      )}
      {showAgentColumn && (
        <td
          style={{
            color: theme.palette.grey[100],
            fontWeight: 600,
            textAlign: 'left',
          }}
        >
          {agentValue}
        </td>
      )}
    </tr>
  );

  const PlanFeatureValue: React.FC<{ text: string }> = ({ text }) => (
    <Box
      sx={{
        flex: 1,
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'row',
        gap: 0.45,
        alignItems: 'flex-start',
      }}
    >
      <span>✓</span>
      <span>{text}</span>
    </Box>
  );

  const renderPlanFeatureValue = (text: string) => <PlanFeatureValue text={text} />;

  const TeaserRow: React.FC<{ setIsOpen: React.Dispatch<React.SetStateAction<boolean>> }> = ({
    setIsOpen,
  }) => {
    const timeLeft = useOfferCountdown(hasActivePlan, hadPreviousActiveSubscription);

    if (timeLeft === '0:00') return null;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: { xs: 1.2, sm: 2 },
          p: { xs: '10px 12px', sm: '12px 16px' },
          borderRadius: '12px',
          cursor: 'pointer',
          // High-end gradient background
          background: `linear-gradient(90deg, rgba(124, 58, 237, 0.1) 0%, rgba(0, 0, 0, 0.2) 100%)`,
          border: `1px solid rgba(124, 58, 237, 0.4)`,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.25)',
          transition: 'all 200ms ease-in-out',
          '&:hover': {
            borderColor: '#a78bfa',
            transform: 'translateY(-1px)',
            background: `linear-gradient(90deg, rgba(124, 58, 237, 0.15) 0%, rgba(0, 0, 0, 0.3) 100%)`,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          {/* Glowing Icon Container */}
          <Box
            sx={{
              display: 'flex',
              p: 1,
              borderRadius: '10px',
              bgcolor: 'rgba(124, 58, 237, 0.2)',
              boxShadow: '0 0 15px rgba(124, 58, 237, 0.3)',
            }}
          >
            <Iconify
              icon={'mdi:gift-outline'}
              width={'24px'} // Increased size
              height={'24px'}
              color={theme.palette.text.highlightedText}
            />
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: { xs: '14px', sm: '18px' },
                fontWeight: 500,
                color: theme.palette.text.primary,
                lineHeight: 1.4,
              }}
            >
              Upgrade now and get{' '}
              <Box
                component="span"
                sx={{ color: theme.palette.text.highlightedText, fontWeight: 800 }}
              >
                +1M bonus credits
              </Box>
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '11px', sm: '12px' },
                color: theme.palette.text.primary,
                opacity: 0.6,
                mt: 0.2,
              }}
            >
              Limited time offer ends soon
            </Typography>
          </Box>
        </Box>

        {/* Right Side: Timer + Action */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            sx={{
              fontSize: { xs: '14px', sm: '18px' },
              fontWeight: 700,
              color: theme.palette.text.highlightedText,
              fontVariantNumeric: 'tabular-nums',
              bgcolor: 'rgba(0,0,0,0.3)',
              px: { xs: 1.1, sm: 1.5 },
              py: 0.5,
              borderRadius: '6px',
              border: `1px solid ${theme.palette.text.highlightedText}40`,
            }}
          >
            {timeLeft}
          </Typography>

          <Button
            variant="contained"
            size="small"
            sx={{
              textTransform: 'none',
              boxShadow: 'none',
              color: theme.palette.text.primary,
              fontWeight: 700,
              fontSize: '13px',
              px: 2,
              whiteSpace: 'nowrap',
            }}
            onClick={() => {
              if (noPlanUser) {
                router.push(`/subscription`);
              } else if (
                enableAgentPlan &&
                ((userPlanData === 'activePlan' && agentPlanData !== 'agentPlan') ||
                  agentPlanData === 'agentTrialPlan')
              ) {
                router.push(`/subscription?name=Everything Agent&from=last`);
              } else {
                router.push(`/subscription?name=All Model Access Plan&from=last`);
              }
              setIsOpen(false);
            }}
          >
            Claim Now
          </Button>
        </Box>
      </Box>
    );
  };

  const LiveTimeBadges: React.FC = () => {
    const timeLeft = useOfferCountdown(hasActivePlan, hadPreviousActiveSubscription);
    const [minutes = '0', seconds = '00'] = timeLeft.split(':');

    return (
      <>
        <TimeBadge value={minutes} label="minutes" />
        <Typography
          sx={{
            fontSize: { xs: '1.2rem', sm: '1.4rem' },
            fontWeight: 700,
            color: theme.palette.grey[400],
            pb: 1.5,
          }}
        >
          :
        </Typography>
        <TimeBadge value={seconds} label="seconds" />
      </>
    );
  };

  const hasComparisonContent = userPlanData !== 'activePlan' || isPaidMainWithoutAgent;

  const navButtonBaseStyle = {
    borderRadius: '8px',
    border: '1px solid #524c58',
    background: '#1F1D27',
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: 600,
    letterSpacing: '0%',
    padding: '10px 16px',
    cursor: 'pointer',
    transition: 'all 180ms ease',
  };

  const primaryStyle = {
    ...navButtonBaseStyle,
    background: '#7B35EF',
    border: '1.5px solid #7B35EF',
    color: '#FFFFFF',
    boxShadow: '0 4px 12px rgba(123, 53, 239, 0.25)',
  };

  const secondaryStyle = {
    ...navButtonBaseStyle,
    background: 'transparent',
    border: '1.5px solid #7B35EF',
    color: '#FFFFFF',
  };

  const shouldRouteTourToAiStudio =
    agentPlanData === 'agentPlan' ||
    agentPlanData === 'agentTrialPlan' ||
    (userPlanData === 'activePlan' && agentPlanData === 'noAgentPlan' && enableAgentPlan);

  const tourLandingPath = shouldRouteTourToAiStudio ? '/ai-studio' : '/text-to-image';

  const getSelector = (): string => {
    return shouldRouteTourToAiStudio
      ? "[data-tut='everything-agent-page']"
      : "[data-tut='text-to-image-page']";
  };

  const waitForSelector = async (selector: string, timeoutMs = 8000): Promise<boolean> => {
    if (typeof document === 'undefined') return false;

    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
      if (document.querySelector(selector)) {
        return true;
      }
      await new Promise((resolve) => window.setTimeout(resolve, 120));
    }

    return !!document.querySelector(selector);
  };

  const syncTourLanding = async (node: Element | null) => {
    if (node) {
      onTourSyncChange?.(false);
      return;
    }

    onTourSyncChange?.(true);

    try {
      const landingSelector = getSelector();
      const hasLandingSelector =
        typeof document !== 'undefined' && !!document.querySelector(landingSelector);

      if (!hasLandingSelector && router.pathname !== tourLandingPath) {
        await router.push(tourLandingPath);
      }

      await waitForSelector(landingSelector);
    } finally {
      onTourSyncChange?.(false);
    }
  };

  const formatMillions = (value: number): string => {
    const millions = value / 1_000_000;
    const formatted = parseFloat(millions.toFixed(2));
    return `${formatted}M`;
  };

  const step1ComparisonTable = hasComparisonContent
    ? [
        {
          selector: getSelector(),
          mutationObservables: [getSelector(), "[data-tut='text-to-image-page']"],
          action: async (node) => {
            await syncTourLanding(node);
          },
          content: ({ setIsOpen }) => (
            <TourPanel size="large">
              <PanelHeader>
                {trialUser ? (
                  <>
                    You're on the free trial - here's what you have and what you unlock when you
                    upgrade
                  </>
                ) : isPaidMainWithoutAgent ? (
                  <>Compare your current All Models plan against Everything Agent</>
                ) : (
                  noPlanUser && (
                    <>You don't have an active plan - here's what you can unlock when you upgrade</>
                  )
                )}
              </PanelHeader>
              {((agentPlanData !== 'agentPlan' && enableAgentPlan) ||
                userPlanData !== 'activePlan') && (
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: '16px',
                    border: `1px solid ${grayBorder}`,
                    background: `linear-gradient(180deg, ${graySurface}, ${graySurfaceElevated})`,
                    p: { xs: 1, sm: 1.5, md: 2 },
                    textAlign: 'left',
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      zIndex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: { xs: 0.75, sm: 1 },
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        borderRadius: '14px',
                        border: `1px solid ${grayBorderSoft}`,
                        background: graySurfaceMuted,
                        p: { xs: 0.75, sm: 1 },
                      }}
                    >
                      <Box
                        component="table"
                        sx={{
                          width: '100%',
                          tableLayout: { xs: 'auto', sm: 'fixed' },
                          borderCollapse: 'collapse',
                          fontSize: { xs: '0.8rem', sm: '1rem' },
                          '& th': {
                            padding: { xs: '6px 6px', sm: '4px 6px' },
                            fontSize: { xs: '13px', sm: '15px' },
                            color: grayTextDim,
                            fontWeight: 500,
                            textAlign: 'left',
                            borderBottom: `0.5px solid ${grayBorderSoft}`,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          },
                          '& th:not(:first-of-type)': {
                            textAlign: 'left',
                          },
                          '& td': {
                            padding: { xs: '6px 6px', sm: '5px 6px' },
                            borderBottom: `0.5px solid ${grayBorderSoft}`,
                            color: grayTextMuted,
                            lineHeight: 1.35,
                            verticalAlign: 'top',
                            fontSize: { xs: '13px', sm: '15px' },
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          },
                          '& td:not(:first-of-type)': {
                            textAlign: 'left',
                          },
                          '& th:first-of-type, & td:first-of-type': {
                            width: {
                              xs: 'auto',
                              sm: trialUser
                                ? '46%'
                                : userPlanData === 'activePlan' &&
                                  agentPlanData !== 'agentPlan' &&
                                  enableAgentPlan
                                ? '40%'
                                : '68%',
                            },
                            whiteSpace: 'normal',
                          },
                          '& th:not(:first-of-type), & td:not(:first-of-type)': {
                            width: {
                              xs: 'auto',
                              sm: trialUser
                                ? '27%'
                                : userPlanData === 'activePlan' &&
                                  agentPlanData !== 'agentPlan' &&
                                  enableAgentPlan
                                ? '30%'
                                : '32%',
                            },
                          },
                          '& th:last-child': {
                            background: theme.palette.background.selectedCardBorder,
                            color: `${theme.palette.text.primary} !important`,
                            fontWeight: 700,
                          },
                          '& td:last-child': {
                            background: `${theme.palette.background.selectedCardBorder}40`,
                            color: `${theme.palette.text.primary} !important`,
                            fontWeight: 600,
                          },
                        }}
                      >
                        <Box component="thead">
                          <Box component="tr">
                            <Box
                              component="th"
                              sx={{ color: `${theme.palette.grey[100]} !important`, opacity: 0.95 }}
                            >
                              Feature
                            </Box>
                            {trialUser && (
                              <Box
                                component="th"
                                sx={{
                                  textAlign: 'left',
                                  color: `${theme.palette.grey[100]} !important`,
                                  opacity: 0.95,
                                }}
                              >
                                Trial
                              </Box>
                            )}
                            {showPaidColumn && (
                              <Box
                                component="th"
                                sx={{
                                  textAlign: 'left',
                                  color: `${theme.palette.grey[100]} !important`,
                                  opacity: 0.95,
                                }}
                              >
                                All Model Access Plan
                              </Box>
                            )}
                            {showAgentColumn && (
                              <Box
                                component="th"
                                sx={{
                                  textAlign: 'left',
                                  color: `${theme.palette.grey[100]} !important`,
                                  opacity: 0.95,
                                }}
                              >
                                Everything Agent Plan
                              </Box>
                            )}
                          </Box>
                        </Box>
                        <Box component="tbody">
                          {showStandardComparisonRows && (
                            <FeatureTableRow
                              name="AI Image Generation"
                              trialValue={trialUser ? '4 Image Generation' : ''}
                              paidValue={renderPlanFeatureValue('')}
                              agentValue={renderPlanFeatureValue('')}
                            />
                          )}

                          {showStandardComparisonRows && (
                            <FeatureTableRow
                              name="Chatbot Access"
                              trialValue={
                                trialUser ? 'Unlimited' : noPlanUser ? 'Upto 5 chats' : ''
                              }
                              paidValue={renderPlanFeatureValue('')}
                              agentValue={renderPlanFeatureValue('')}
                            />
                          )}

                          {showStandardComparisonRows && (
                            <FeatureTableRow
                              name="AI Video Generation"
                              trialValue={trialUser ? '✕' : ''}
                              paidValue={renderPlanFeatureValue('')}
                              agentValue={renderPlanFeatureValue('')}
                              locked
                            />
                          )}

                          {showStandardComparisonRows && (
                            <FeatureTableRow
                              name="AI Clipper"
                              trialValue={trialUser ? '✕' : ''}
                              paidValue={renderPlanFeatureValue('')}
                              agentValue={renderPlanFeatureValue('')}
                              locked
                            />
                          )}

                          {showStandardComparisonRows && (
                            <FeatureTableRow
                              name="LipSync & Talking Avatars"
                              trialValue={trialUser ? '✕' : ''}
                              paidValue={renderPlanFeatureValue('')}
                              agentValue={renderPlanFeatureValue('')}
                              locked
                            />
                          )}

                          {showStandardComparisonRows && (
                            <FeatureTableRow
                              name="Captions & Effects for Videos"
                              trialValue={trialUser ? '✕' : ''}
                              paidValue={renderPlanFeatureValue('')}
                              agentValue={renderPlanFeatureValue('')}
                              locked
                            />
                          )}

                          {isPaidMainWithoutAgent && (
                            <FeatureTableRow
                              includeTrialColumn={false}
                              name="Batch Image/Video Processing"
                              paidValue={'-'}
                              agentValue={renderPlanFeatureValue(
                                'Generate in bulk while you keep working'
                              )}
                            />
                          )}

                          {isPaidMainWithoutAgent && (
                            <FeatureTableRow
                              includeTrialColumn={false}
                              name="Background Queue"
                              paidValue={'-'}
                              agentValue={renderPlanFeatureValue(
                                'Queue jobs and run them in the background'
                              )}
                            />
                          )}

                          {isPaidMainWithoutAgent && (
                            <FeatureTableRow
                              includeTrialColumn={false}
                              name="Agent Credits"
                              paidValue={'-'}
                              agentValue={renderPlanFeatureValue(
                                isYearlyMain
                                  ? `${formatMillions(
                                      nextPlanData.agentAddonCredits * 12
                                    )} agent credits per year`
                                  : `${formatMillions(
                                      nextPlanData.agentAddonCredits
                                    )} agent credits per month`
                              )}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <button
                  type="button"
                  style={primaryStyle}
                  onClick={() => {
                    if (noPlanUser) {
                      router.push(`/subscription`);
                    } else if (
                      enableAgentPlan &&
                      ((userPlanData === 'activePlan' && agentPlanData !== 'agentPlan') ||
                        agentPlanData === 'agentTrialPlan')
                    ) {
                      router.push(`/subscription?name=Everything Agent&from=last`);
                    } else {
                      router.push(`/subscription?name=All Model Access Plan&from=last`);
                    }
                    setIsOpen(false);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.filter = 'brightness(1.08)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.filter = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <span>
                    Upgrade Now to{' '}
                    {userPlanData === 'activePlan' &&
                    agentPlanData !== 'agentPlan' &&
                    enableAgentPlan
                      ? 'Agent Plan'
                      : 'Fully Paid'}{' '}
                    <span style={{ fontSize: '16px' }}>✨</span>
                  </span>
                </button>
              </Box>
            </TourPanel>
          ),
        },
      ]
    : [];

  return [
    {
      selector: getSelector(),
      mutationObservables: [getSelector(), "[data-tut='text-to-image-page']"],
      action: async (node) => {
        await syncTourLanding(node);
      },
      content: ({ setIsOpen, setCurrentStep }) => (
        <TourPanel size="large">
          <PanelHeader>
            {isPaidMainWithoutAgent ? 'Upgrade' : 'Welcome to the Txt2Create'}
          </PanelHeader>
          {isPaidMainWithoutAgent ? (
            <>
              <Box
                sx={{
                  borderRadius: '14px',
                  border: `1px solid ${grayBorder}`,
                  background: `linear-gradient(180deg, ${graySurface}, ${graySurfaceElevated})`,
                  p: { xs: 1, sm: 1.4 },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.2,
                  height: { xs: '45vh', sm: 'auto' },
                  overflowY: 'auto',
                  [compactViewportQuery]: {
                    p: 0.45,
                    height: '50vh',
                  },
                }}
              >
                <Typography sx={{ fontSize: { xs: '1.35rem', sm: '1.75rem' }, fontWeight: 600 }}>
                  Add Everything Agent for background batch creation.
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, color: grayTextMuted }}>
                  Use it when you want jobs queued, batched, and running while you work.
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                    gap: 1,
                  }}
                >
                  {AGENT_UPSELL_STATS.map(({ value, label, sublabel }) => (
                    <Box
                      key={value}
                      sx={{
                        borderRadius: '10px',
                        border: `0.5px solid ${grayBorderSoft}`,
                        background: graySurfaceMuted,
                        py: 1,
                        px: 0.7,
                        textAlign: 'center',
                      }}
                    >
                      <Typography sx={{ fontSize: '1.8rem', color: '#A78BFA', fontWeight: 700 }}>
                        {value}
                      </Typography>
                      <Typography
                        sx={{ color: theme.palette.grey[100], fontWeight: 600, fontSize: '1rem' }}
                      >
                        {label}
                      </Typography>
                      <Typography sx={{ color: grayTextSubtle, fontSize: '0.95rem' }}>
                        {sublabel}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box
                  sx={{
                    borderRadius: '12px',
                    border: `1px solid ${theme.palette.text.highlightedText}`,
                    p: 1.15,
                    background: graySurfaceMuted,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: '0.95rem', sm: '1.2rem' },
                          color: theme.palette.grey[100],
                          textAlign: 'left',
                        }}
                      >
                        Everything Agent
                      </Typography>
                      <Typography
                        sx={{
                          color: grayTextSubtle,
                          fontSize: { xs: '0.85rem', sm: '1rem' },
                          textAlign: 'left',
                        }}
                      >
                        Switch to Everything Agent plan now.
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: '1.2rem', sm: '1.8rem' },
                          color: '#A78BFA',
                        }}
                      >
                        {displayAgentAmount !== null
                          ? `$${displayAgentAmount}/${billingSuffix}`
                          : renderAmountSkeleton(110)}
                      </Typography>
                      <Typography
                        sx={{ color: grayTextSubtle, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}
                      >
                        Charged now
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {(isYearlyMain
                      ? [
                          `${formatMillions(
                            nextPlanData.agentAddonCredits * 12
                          )} agent credits/yr - batch-generate at scale`,
                          ...AGENT_UPSELL_FEATURES,
                        ]
                      : [
                          `${formatMillions(
                            nextPlanData.agentAddonCredits
                          )} agent credits/mo - batch-generate at scale`,
                          ...AGENT_UPSELL_FEATURES,
                        ]
                    ).map((feature) => (
                      <Typography
                        key={feature}
                        sx={{
                          color: theme.palette.grey[100],
                          fontSize: { xs: '0.95rem', sm: '1rem' },
                          display: 'flex',
                          gap: 0.6,
                          textAlign: 'left',
                        }}
                      >
                        <span>◌</span>
                        <span>{feature}</span>
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  borderRadius: '11px',
                  border: `0.5px solid ${grayBorderSoft}`,
                  background: graySurfaceMuted,
                  p: 1.1,
                  my: 0.5,
                }}
              >
                <Typography
                  sx={{
                    color: theme.palette.grey[100],
                    fontWeight: 700,
                    mb: 0.5,
                    textAlign: 'left',
                  }}
                >
                  Checkout summary
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    color: grayTextMuted,
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    py: 0.15,
                  }}
                >
                  <span>Everything Agent plan</span>
                  <span>
                    {displayAgentAmount !== null
                      ? `$${displayAgentAmount}/${billingSuffix}`
                      : renderAmountSkeleton()}
                  </span>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    color: grayTextMuted,
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    py: 0.35,
                  }}
                >
                  <span>Current plan</span>
                  <span style={{ textAlign: 'right' }}>Ends next billing cycle</span>
                </Box>
                <Box
                  sx={{
                    color: grayTextSubtle,
                    fontSize: { xs: '0.82rem', sm: '0.9rem' },
                    lineHeight: 1.4,
                    mt: 0.25,
                  }}
                >
                  You are charged now for Everything Agent. Your previous plan will not renew, and
                  remaining credits stay available until its renewal date.
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <button
                  type="button"
                  style={{ ...primaryStyle, width: '100%' }}
                  onClick={() => {
                    router.push('/subscription?name=Everything Agent&from=last');
                    setIsOpen(false);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.filter = 'brightness(1.08)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.filter = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Upgrade to Everything Agent ✨
                </button>
                <button
                  type="button"
                  style={{ ...navButtonBaseStyle, width: '100%' }}
                  onClick={() => setCurrentStep(1)}
                >
                  Continue with current plan
                </button>
              </Box>
            </>
          ) : agentPlanData === 'agentPlan' ? null : (
            <Box
              sx={{
                position: 'relative',
                borderRadius: '16px',
                border: `1px solid ${grayBorder}`,
                background: `linear-gradient(180deg, ${graySurface}, ${graySurfaceElevated})`,
                p: { xs: 0.8, sm: 1.4, md: 2 },
                pb: { xs: 0.8, sm: 1.4, md: '8px !important' },
                textAlign: 'left',
                [compactViewportQuery]: {
                  p: 0.65,
                },
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: 0.6, sm: 1 },
                  [compactViewportQuery]: {
                    gap: 0.5,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '0.84rem', sm: '1.2rem' },
                    color: grayTextMuted,
                    textAlign: 'center',
                    lineHeight: 1.4,
                    mx: 'auto',
                    [compactViewportQuery]: {
                      fontSize: '0.8rem',
                      lineHeight: 1.35,
                    },
                  }}
                >
                  {trialUser ? (
                    <>Upgrade To A Fully Paid Account Now</>
                  ) : userPlanData === 'activePlan' &&
                    agentPlanData !== 'agentPlan' &&
                    enableAgentPlan ? (
                    <>Upgrade to Everything Agent Plan Now</>
                  ) : (
                    noPlanUser && <>Purchase a Fully Paid Plan Now</>
                  )}
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: '0.74rem', sm: '1.08rem' },
                    color: grayTextSubtle,
                    textAlign: 'center',
                    lineHeight: 1.45,
                    mx: 'auto',
                    [compactViewportQuery]: {
                      fontSize: '0.7rem',
                      lineHeight: 1.38,
                    },
                  }}
                >
                  {userPlanData == 'activePlan' &&
                  agentPlanData !== 'agentPlan' &&
                  enableAgentPlan ? (
                    <>
                      Upgrade to the Everything Agent Plan to unlock all features including
                      high-impact scripted ads, batch processing, and workflow efficiency.
                    </>
                  ) : noPlanUser ? (
                    <>
                      You currently don't have an active plan, which means many models and features
                      are unavailable. You can browse to see if we have all the features that you
                      need or you can finish paying for your account to get unrestricted access to
                      all models and extra credit bonuses.
                    </>
                  ) : (
                    <>
                      You're currently on a free trial where many models and features are
                      unavailable. You can browse to see if we have all the features that you need
                      or you can finish paying for your account to get unrestricted access to all
                      models and extra credit bonuses.
                    </>
                  )}
                </Typography>

                <Box
                  sx={{
                    width: '100%',
                    borderRadius: '14px',
                    border: `1px solid ${grayBorderSoft}`,
                    background: graySurfaceMuted,
                    p: { xs: 0.55, sm: 0.95 },
                    height: {
                      xs: '18vh',
                      sm: agentPlanData === 'agentTrialPlan' ? '28vh' : 'auto',
                    },
                    overflowY: 'auto',
                    [compactViewportQuery]: {
                      p: 0.45,
                      height: '20vh',
                    },
                  }}
                >
                  <Box
                    component="table"
                    sx={{
                      width: '100%',
                      tableLayout: { xs: 'auto', sm: 'fixed' },
                      borderCollapse: 'collapse',
                      fontSize: { xs: '0.72rem', sm: '1rem' },
                      '& th': {
                        padding: { xs: '4px 4px', sm: '4px 6px' },
                        fontSize: { xs: '11px', sm: '14px' },
                        color: grayTextDim,
                        fontWeight: 500,
                        textAlign: 'left',
                        borderBottom: `0.5px solid ${grayBorderSoft}`,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                      },
                      '& th:not(:first-of-type)': {
                        textAlign: 'left',
                      },
                      '& td': {
                        padding: { xs: '4px 4px', sm: '5px 6px' },
                        borderBottom: `0.5px solid ${grayBorderSoft}`,
                        color: grayTextMuted,
                        lineHeight: 1.25,
                        verticalAlign: 'middle',
                        fontSize: { xs: '11px', sm: '14px' },
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                      },
                      '& td:not(:first-of-type)': {
                        textAlign: 'left',
                      },
                      '& th:first-of-type, & td:first-of-type': {
                        width: { xs: 'auto', sm: firstStepNameColumnWidth },
                        whiteSpace: 'normal',
                      },
                      '& th:not(:first-of-type), & td:not(:first-of-type)': {
                        width: { xs: 'auto', sm: firstStepValueColumnWidth },
                      },
                    }}
                  >
                    <Box component="thead">
                      <Box component="tr">
                        <Box
                          component="th"
                          sx={{ color: `${theme.palette.grey[100]} !important`, opacity: 0.95 }}
                        >
                          Feature
                        </Box>
                        {!showAgentColumn && (
                          <Box
                            component="th"
                            sx={{
                              textAlign: 'left',
                              color: `${theme.palette.grey[100]} !important`,
                              opacity: 0.95,
                            }}
                          >
                            With Fully Paid Plan
                          </Box>
                        )}
                        {showAgentColumn && (
                          <Box
                            component="th"
                            sx={{
                              textAlign: 'left',
                              color: `${theme.palette.grey[100]} !important`,
                              opacity: 0.95,
                            }}
                          >
                            With A Fully Paid Everything Agent Plan
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {showAgentColumn && (
                        <FeatureTableRow
                          includeTrialColumn={false}
                          name="Bulk UGC Generation"
                          paidValue={''}
                          agentValue={renderPlanFeatureValue(
                            'Generate 20+ UGC videos from one product upload'
                          )}
                          locked
                        />
                      )}

                      {showAgentColumn && (
                        <FeatureTableRow
                          includeTrialColumn={false}
                          name="Batch Video Creation"
                          paidValue={''}
                          agentValue={renderPlanFeatureValue(
                            'Create 50 short-form clips in one batch'
                          )}
                        />
                      )}

                      {showAgentColumn && (
                        <FeatureTableRow
                          includeTrialColumn={false}
                          name="Bulk Image Creation"
                          paidValue={''}
                          agentValue={renderPlanFeatureValue('Generate 100+ image ads instantly')}
                        />
                      )}

                      {showAgentColumn && (
                        <FeatureTableRow
                          includeTrialColumn={false}
                          name="Background Queue"
                          paidValue={'-'}
                          agentValue={renderPlanFeatureValue(
                            'Queue jobs and run them in the background'
                          )}
                        />
                      )}

                      {showAgentColumn && (
                        <FeatureTableRow
                          includeTrialColumn={false}
                          name="Parallel Generations"
                          paidValue={'-'}
                          agentValue={renderPlanFeatureValue(
                            'Run multiple image and video jobs at once'
                          )}
                        />
                      )}

                      {showAgentColumn && (
                        <FeatureTableRow
                          includeTrialColumn={false}
                          name="Social Media Content"
                          paidValue={'-'}
                          agentValue={renderPlanFeatureValue(
                            'Bulk-create Social Media content and ad creatives faster'
                          )}
                        />
                      )}

                      {showAgentColumn && (
                        <FeatureTableRow
                          includeTrialColumn={false}
                          name="Agnostic Agent"
                          paidValue={'-'}
                          agentValue={renderPlanFeatureValue(
                            'All models work together agentically in the background'
                          )}
                        />
                      )}

                      {showAgentColumn && (
                        <FeatureTableRow
                          includeTrialColumn={false}
                          name="MCP Server"
                          paidValue={'-'}
                          agentValue={renderPlanFeatureValue(
                            'Connect with any MCP-compatible AI agent using our agent MCP'
                          )}
                        />
                      )}

                      {showAgentColumn && (
                        <FeatureTableRow
                          includeTrialColumn={false}
                          name="Agent Credits"
                          paidValue={'-'}
                          agentValue={renderPlanFeatureValue(
                            isYearlyMain
                              ? `${formatMillions(
                                  nextPlanData.agentAddonCredits * 12
                                )} agent credits per year`
                              : `${formatMillions(
                                  nextPlanData.agentAddonCredits
                                )} agent credits per month`
                          )}
                        />
                      )}

                      {showStandardComparisonRows && (
                        <FeatureTableRow
                          includeTrialColumn={false}
                          name={'Premium Models'}
                          paidValue={renderPlanFeatureValue('All Models Unlocked')}
                          agentValue={renderPlanFeatureValue('All Models Unlocked')}
                        />
                      )}

                      {showStandardComparisonRows && (
                        <FeatureTableRow
                          includeTrialColumn={false}
                          name={'Models Included'}
                          paidValue={
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: { xs: 0.25, sm: 0.75 },
                                flexWrap: 'wrap',
                              }}
                            >
                              {['Seedance 2.5', 'Kling 3', 'Nano Banana Pro', 'GPT Image 2'].map(
                                (model) => (
                                  <Box
                                    key={model}
                                    component="span"
                                    sx={{
                                      display: 'block',
                                      opacity: 0.85,
                                      fontSize: { xs: '9px', sm: '12px' },
                                      whiteSpace: 'nowrap',
                                      background: graySurfaceSoft,
                                      padding: '5px',
                                      borderRadius: '6px',
                                    }}
                                  >
                                    {model}
                                  </Box>
                                )
                              )}
                            </Box>
                          }
                          agentValue={
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: { xs: 0.25, sm: 0.75 },
                                flexWrap: 'wrap',
                              }}
                            >
                              {['Seedance 2.5', 'Kling 3', 'Nano Banana Pro', 'GPT Image 2'].map(
                                (model) => (
                                  <Box
                                    key={model}
                                    component="span"
                                    sx={{
                                      display: 'block',
                                      opacity: 0.85,
                                      fontSize: { xs: '9px', sm: '12px' },
                                      whiteSpace: 'nowrap',
                                      background: graySurfaceSoft,
                                      padding: '5px',
                                      borderRadius: '6px',
                                    }}
                                  >
                                    {model}
                                  </Box>
                                )
                              )}
                            </Box>
                          }
                        />
                      )}

                      {showStandardComparisonRows && (
                        <FeatureTableRow
                          includeTrialColumn={false}
                          name="Commercial Export License"
                          paidValue={renderPlanFeatureValue(
                            'Use for Ads, UGC & Commercial Projects'
                          )}
                          agentValue={renderPlanFeatureValue(
                            'Use for Ads, UGC & Commercial Projects'
                          )}
                          locked
                        />
                      )}

                      {showStandardComparisonRows && (
                        <FeatureTableRow
                          includeTrialColumn={false}
                          name="Video Quality & Duration"
                          paidValue={renderPlanFeatureValue(
                            'Generate longer, higher-quality videos and images'
                          )}
                          agentValue={renderPlanFeatureValue(
                            'Generate longer, higher-quality videos and images'
                          )}
                          locked
                        />
                      )}

                      {showStandardComparisonRows && (
                        <FeatureTableRow
                          includeTrialColumn={false}
                          name="Processing Speed"
                          paidValue={renderPlanFeatureValue(
                            'Skip the wait with priority processing speed'
                          )}
                          agentValue={renderPlanFeatureValue(
                            'Skip the wait with priority processing speed'
                          )}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>

                {agentPlanData === 'agentTrialPlan' && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexDirection: 'column',
                      gap: { xs: 0.6, sm: 0.9 },
                      border: `1px solid ${theme.palette.background.activeBorder}`,
                      borderRadius: '10px',
                      px: { xs: 0.9, sm: 1.4 },
                      py: { xs: 0.55, sm: 0.85 },
                      my: 0.5,
                      [compactViewportQuery]: {
                        px: 0.7,
                        py: 0.45,
                        gap: 0.5,
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.95rem' },
                        color: theme.palette.text.lighterText2,
                        lineHeight: 1.4,
                        fontWeight: 600,
                        textAlign: 'center',
                        [compactViewportQuery]: { fontSize: '0.68rem', lineHeight: 1.3 },
                      }}
                    >
                      CURRENTLY YOU CAN ONLY GENERATE 4 IMAGES WITH THE AGENT WHILE IN YOUR FREE
                      TRIAL
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.68rem', sm: '0.88rem' },
                        color: theme.palette.text.lighterText,
                        lineHeight: 1.45,
                        mt: 0.3,
                        textAlign: 'center',
                        [compactViewportQuery]: { fontSize: '0.62rem', lineHeight: 1.25 },
                      }}
                    >
                      Upgrade now for video generation, unlimited images, and unrestricted access to
                      all agentic features.
                    </Typography>
                  </Box>
                )}

                {userPlanData !== 'activePlan' && <TeaserRow setIsOpen={setIsOpen} />}
              </Box>
            </Box>
          )}
          {(trialUser || noPlanUser) && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
              <button
                type="button"
                style={primaryStyle}
                onClick={() => {
                  if (noPlanUser) {
                    router.push(`/subscription`);
                  } else if (
                    enableAgentPlan &&
                    ((userPlanData === 'activePlan' && agentPlanData !== 'agentPlan') ||
                      agentPlanData === 'agentTrialPlan')
                  ) {
                    router.push(`/subscription?name=Everything Agent&from=last`);
                  } else {
                    router.push(`/subscription?name=All Model Access Plan&from=last`);
                  }
                  setIsOpen(false);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.filter = 'brightness(1.08)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.filter = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  Upgrade Now to {agentPlanData === 'agentTrialPlan' ? 'Agent Plan' : 'Fully Paid'}
                  <span style={{ fontSize: '18px' }}>✨</span>
                </span>
              </button>

              {/* Sub-heading Pricing Details */}
              {noPlanUser
                ? null
                : (nextPlanData.amount > 0 || nextPlanData.agentAddonAmount > 0) && (
                    <Box
                      sx={{
                        marginTop: '10px',
                        textAlign: 'center',
                        fontSize: { xs: '10px', sm: '13px' },
                        color: grayTextSubtle,
                        maxWidth: '320px',
                        lineHeight: '1.5',
                      }}
                    >
                      {nextPlanData.interval === 'Monthly' ? (
                        <>
                          <Box sx={{ fontSize: { xs: '12px', sm: '16px' }, marginBottom: '2px' }}>
                            Pay{' '}
                            <b style={{ color: theme.palette.text.primary }}>
                              {agentPlanData === 'agentTrialPlan'
                                ? `$${nextPlanData.agentAddonAmount}/mo`
                                : `$${nextPlanData.amount}/mo`}
                            </b>
                          </Box>

                          <Box>
                            <Typography
                              component="span"
                              sx={{
                                fontSize: { xs: '11px', sm: '15px' },
                                color: theme.palette.text.highlightedText,
                                fontWeight: 600,
                              }}
                            >
                              {agentPlanData === 'agentTrialPlan'
                                ? `${formatMillions(nextPlanData.agentAddonCredits)}`
                                : nextPlanData.credits}{' '}
                              Credits
                            </Typography>{' '}
                            • Full {showAgentColumn ? 'Agent' : 'Model'} Access
                          </Box>
                        </>
                      ) : (
                        <>
                          <Box sx={{ fontSize: { xs: '12px', sm: '16px' }, marginBottom: '2px' }}>
                            Pay{' '}
                            <b style={{ color: theme.palette.text.primary }}>
                              {agentPlanData === 'agentTrialPlan'
                                ? `$${nextPlanData.agentAddonAmount}/yr`
                                : `$${nextPlanData.amount}/yr`}
                            </b>
                          </Box>

                          <Box sx={{ fontSize: { xs: '11px', sm: '15px' } }}>
                            <b style={{ color: theme.palette.text.highlightedText }}>
                              {agentPlanData === 'agentTrialPlan'
                                ? `${formatMillions(nextPlanData.agentAddonCredits * 12)}`
                                : nextPlanData.credits}{' '}
                              Credits
                            </b>{' '}
                            • Full {showAgentColumn ? 'Agent' : 'Model'} Access
                          </Box>
                        </>
                      )}
                    </Box>
                  )}
            </Box>
          )}
        </TourPanel>
      ),
    },

    ...step1ComparisonTable,

    {
      selector: "[data-tut='create-ai-text']",
      mutationObservables: ["[data-tut='create-ai-text']", "[key='create-ai-text']"],
      action: async (node) => {
        await syncTourLanding(node);
      },
      content: (
        <TourPanel size="small">
          <PanelHeader>AI Workspace</PanelHeader>
          <PanelBody>
            {trialUser
              ? 'Explore our AI chatbot and try different models to experience the platform’s capabilities.'
              : noPlanUser
              ? 'Discover our AI chatbot and start exploring intelligent conversations with ease.'
              : 'Enjoy seamless access to our AI chatbot and switch between models to power your ideas.'}
          </PanelBody>
        </TourPanel>
      ),
    },
    {
      selector: "[data-tut='create-image']",
      action: async (node) => {
        await syncTourLanding(node);
      },
      content: (
        <TourPanel size="medium">
          <PanelHeader>Image Generation Studio</PanelHeader>
          <PanelBody>Explore our expanding library of AI image models.</PanelBody>
          <PanelHighlight>
            {trialUser ? (
              <Typography>
                Try out our AI image models and experience the creative possibilities they offer.
              </Typography>
            ) : noPlanUser ? (
              <Typography>
                Discover our AI-powered image generation tools and explore a wide range of creative
                styles.
              </Typography>
            ) : (
              <Typography>
                Enjoy seamless access to our AI image models and create stunning visuals for your
                projects.
              </Typography>
            )}
          </PanelHighlight>
        </TourPanel>
      ),
    },
    {
      selector: "[data-tut='create-broll']",
      action: async (node) => {
        await syncTourLanding(node);
      },
      content: ({ currentStep, setIsOpen }) => (
        <TourPanel size="medium">
          <PanelHeader>Video Generation Studio</PanelHeader>
          <PanelBody>Explore our frequently updated library of video generation models.</PanelBody>
          <PanelHighlight>
            {userPlanData === 'activePlan' ? (
              <Typography>
                Access a growing collection of video generation models and create dynamic visuals
                for your projects.
              </Typography>
            ) : (
              <Typography>
                Discover our video generation models and explore the possibilities of AI-powered
                video creation.
              </Typography>
            )}
          </PanelHighlight>
          {userPlanData !== 'activePlan' && (
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
              <Button
                size="small"
                sx={{
                  animation: 'shake 0.6s ease-in-out infinite',
                  animationDelay: '4s',
                  animationIterationCount: 'infinite',
                  '@keyframes shake': {
                    '0%': { transform: 'translateX(0)' },
                    '20%': { transform: 'translateX(-3px)' },
                    '40%': { transform: 'translateX(3px)' },
                    '60%': { transform: 'translateX(-3px)' },
                    '80%': { transform: 'translateX(3px)' },
                    '100%': { transform: 'translateX(0)' },
                  },
                }}
                onClick={() => {
                  if (noPlanUser) {
                    router.push(`/subscription`);
                  } else if (
                    enableAgentPlan &&
                    ((userPlanData === 'activePlan' && agentPlanData !== 'agentPlan') ||
                      agentPlanData === 'agentTrialPlan')
                  ) {
                    router.push(`/subscription?name=Everything Agent&from=last`);
                  } else {
                    router.push(`/subscription?name=All Model Access Plan&from=last`);
                  }
                  setIsOpen(false);
                  localStorage.setItem(tourStepKey, currentStep.toString());
                  localStorage.setItem(tourInitializedKey, 'true');
                }}
              >
                Upgrade
              </Button>
            </Box>
          )}
        </TourPanel>
      ),
    },
    {
      selector: "[data-tut='ai-clipper']",
      action: async (node) => {
        await syncTourLanding(node);
      },
      content: (
        <TourPanel size="medium">
          <PanelHeader>AI Clipper</PanelHeader>
          <PanelBody>
            Instantly transform long-form content into viral, platform-ready shorts with a single
            click.
          </PanelBody>
          {userPlanData !== 'activePlan' && (
            <PanelHighlight>
              This premium tool is available exclusively with a paid subscription.
            </PanelHighlight>
          )}
        </TourPanel>
      ),
    },
    {
      selector: "[data-tut='talking-face']",
      action: async (node) => {
        await syncTourLanding(node);
      },
      content: (
        <TourPanel size="medium">
          <PanelHeader>Talking Face & Avatars</PanelHeader>
          <PanelBody>
            Powered by HeyGen and Hedra, this tool allows you to generate realistic talking avatars
            in seconds.
          </PanelBody>
          {userPlanData !== 'activePlan' && (
            <PanelHighlight>Upgrade your account to start creating.</PanelHighlight>
          )}
        </TourPanel>
      ),
    },
    {
      selector: "[data-tut='optimize-broll']",
      mutationObservables: ["[data-tut='optimize-broll']", "[key='optimize-broll']"],
      action: async (node) => {
        await syncTourLanding(node);
      },
      content: (
        <TourPanel size="medium">
          <PanelHeader>Captions & Effects</PanelHeader>
          <PanelBody>
            Instantly add dynamic captions and relevant B-roll to any video using advanced AI.
          </PanelBody>
          {userPlanData !== 'activePlan' && (
            <PanelHighlight>
              Start creating professional-grade content by upgrading your subscription today.
            </PanelHighlight>
          )}
        </TourPanel>
      ),
    },
    {
      selector: getSelector(),
      mutationObservables: [getSelector()],
      action: async (node) => {
        await syncTourLanding(node);
      },
      content: ({ setIsOpen }) => {
        const timeLeft = useOfferCountdown(hasActivePlan, hadPreviousActiveSubscription);
        return (
          <TourPanel size="large">
            <PanelHeader>You have finished your basic tour!</PanelHeader>
            {!hasActivePlan && timeLeft !== '0:00' && (
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: '16px',
                  border: `1px solid ${grayBorder}`,
                  background: `linear-gradient(180deg, ${graySurface}, ${graySurfaceElevated})`,
                  p: { xs: 1.5, sm: 2, md: 2.5 },
                  textAlign: 'center',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: 1.5, sm: 2 },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      fontSize: '13px',
                      px: 1.2,
                      py: 0.35,
                      borderRadius: '999px',
                      background: graySurfaceSoft,
                      border: `0.5px solid ${grayBorderSoft}`,
                      color: theme.palette.grey[200],
                      mx: 'auto',
                    }}
                  >
                    ✦ Limited-time offer
                  </Box>
                  <Typography
                    sx={{
                      color: theme.palette.grey[100],
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      fontWeight: 500,
                      mb: 0.75,
                      maxWidth: 460,
                      mx: 'auto',
                    }}
                  >
                    Get +1M extra credits free
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: grayTextMuted,
                      lineHeight: 1.65,
                      maxWidth: 600,
                      mx: 'auto',
                    }}
                  >
                    Upgrade to the full plan within the next 10 minutes and we'll add 1M bonus
                    credits to your account.
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: { xs: 0.75, sm: 1 },
                      flexWrap: 'wrap',
                    }}
                  >
                    <LiveTimeBadges />
                  </Box>

                  <Button
                    sx={{
                      width: '100%',
                      py: 1.15,
                      borderRadius: '999px',
                      background: `linear-gradient(180deg, ${theme.palette.grey[100]}, ${theme.palette.grey[300]})`,
                      color: theme.palette.grey[900],
                      fontSize: '13px',
                      fontWeight: 500,
                      textAlign: 'center',
                      boxShadow: `0 10px 20px ${grayShadow}`,
                      border: `1px solid ${alpha(theme.palette.grey[100], 0.18)}`,
                    }}
                    onClick={() => {
                      if (noPlanUser) {
                        router.push(`/subscription`);
                      } else if (
                        enableAgentPlan &&
                        ((userPlanData === 'activePlan' && agentPlanData !== 'agentPlan') ||
                          agentPlanData === 'agentTrialPlan')
                      ) {
                        router.push(`/subscription?name=Everything Agent&from=last`);
                      } else {
                        router.push(`/subscription?name=All Model Access Plan&from=last`);
                      }
                      setIsOpen(false);
                      localStorage.removeItem(tourStepKey);
                      onChangeWelcomeTour(getTourCompletedVersion(tourUserEmail));
                      setTimeout(() => localStorage.removeItem(tourInitializedKey), 350);
                    }}
                  >
                    Upgrade and claim 1M bonus credits ✦
                  </Button>
                </Box>
              </Box>
            )}
          </TourPanel>
        );
      },
    },
    // {
    //   selector: "[data-tut='text-to-image-page']",
    //   content: (
    //     <TourPanel size="large">
    //       <PanelHeader>Text-to-Image Studio</PanelHeader>
    //       <PanelBody>Transform your ideas into high-resolution visuals.</PanelBody>
    //       <PanelHighlight>
    //         {userPlanData === 'freeTrialAccess' ? (
    //           <Typography>
    //             Try creating high-resolution visuals with our AI models and explore the creative
    //             possibilities they offer.
    //           </Typography>
    //         ) : userPlanData === 'noActivePlan' ? (
    //           <Typography>
    //             Discover how our AI models can transform your ideas into detailed and visually
    //             compelling images.
    //           </Typography>
    //         ) : (
    //           <Typography>
    //             Bring your ideas to life with powerful AI models designed to generate high-quality,
    //             custom visuals for your projects.
    //           </Typography>
    //         )}
    //       </PanelHighlight>
    //     </TourPanel>
    //   ),
    //   mutationObservables: ["[data-tut='text-to-image-page']", '#text-to-image-page'],
    //   disableActions: false,
    // },
    // {
    //   selector: "[data-tut='text-to-image-models']",
    //   content: ({ currentStep, setIsOpen }) => (
    //     <TourPanel size="medium">
    //       <PanelHeader>Advanced Image Models</PanelHeader>
    //       <PanelBody>
    //         {userPlanData === 'freeTrialAccess' ? (
    //           <Typography>
    //             Explore a selection of our AI image models and see how they can bring your ideas to
    //             life.
    //           </Typography>
    //         ) : userPlanData === 'noActivePlan' ? (
    //           <Typography>
    //             Browse our growing catalog of AI image models designed for creative and professional
    //             visuals.
    //           </Typography>
    //         ) : (
    //           <Typography>
    //             Choose from a wide range of AI image models and start creating high-quality visuals
    //             for your projects.
    //           </Typography>
    //         )}
    //       </PanelBody>

    //       {userPlanData === 'freeTrialAccess' && (
    //         <PanelHighlight>
    //           <Typography>
    //             Experiment with different models and explore the creative possibilities of
    //             AI-powered image generation.
    //           </Typography>
    //         </PanelHighlight>
    //       )}
    //       {userPlanData === 'noActivePlan' && (
    //         <PanelHighlight>
    //           <Typography>
    //             Discover how our AI tools can help transform your ideas into unique visual content.
    //           </Typography>
    //         </PanelHighlight>
    //       )}
    //       {userPlanData !== 'activePlan' && (
    //         <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
    //           <Button
    //             size="small"
    //             sx={{
    //               animation: 'shake 0.6s ease-in-out infinite',
    //               animationDelay: '4s',
    //               animationIterationCount: 'infinite',
    //               '@keyframes shake': {
    //                 '0%': { transform: 'translateX(0)' },
    //                 '20%': { transform: 'translateX(-3px)' },
    //                 '40%': { transform: 'translateX(3px)' },
    //                 '60%': { transform: 'translateX(-3px)' },
    //                 '80%': { transform: 'translateX(3px)' },
    //                 '100%': { transform: 'translateX(0)' },
    //               },
    //             }}
    //             onClick={() => {
    //               router.push('/subscription');
    //               setIsOpen(false);
    //               localStorage.setItem('tourStep', currentStep.toString());
    //             }}
    //           >
    //             Subscribe
    //           </Button>
    //         </Box>
    //       )}
    //     </TourPanel>
    //   ),
    // },
    // {
    //   selector: "[data-tut='text-to-image-prompt']",
    //   content: (
    //     <TourPanel size="medium">
    //       <PanelHeader>Image Generation Prompt</PanelHeader>
    //       <PanelBody>Describe your vision here to begin.</PanelBody>
    //       <PanelHighlight>
    //         Use our AI Enhancement tool to automatically refine your prompt for better detail and
    //         lighting.
    //       </PanelHighlight>
    //     </TourPanel>
    //   ),
    // },
    // {
    //   selector: "[data-tut='text-to-image-reference-image']",
    //   content: (
    //     <TourPanel size="small">
    //       <PanelHeader>Image-to-Image Guidance</PanelHeader>
    //       <PanelBody>
    //         Upload a reference image to guide the AI’s composition and style. The model will analyze
    //         your reference images to generate a high-quality output tailored to your specific
    //         vision.
    //       </PanelBody>
    //     </TourPanel>
    //   ),
    // },
    // {
    //   selector: "[data-tut='text-to-image-properties']",
    //   content: ({ setIsOpen }) => (
    //     <TourPanel size="large">
    //       <PanelHeader>Advanced Generation Control</PanelHeader>
    //       <PanelBody>
    //         Fine-tune your results by adjusting image properties and generation counts.
    //       </PanelBody>
    //       <PanelHighlight>
    //         {userPlanData === 'freeTrialAccess' ? (
    //           <Typography>
    //             Available customization options will adapt based on the specific model you select.
    //             You can currently generate one image with your free trial access.
    //           </Typography>
    //         ) : userPlanData === 'noActivePlan' ? (
    //           <Typography>
    //             To enable these customization tools and start generating visuals, please subscribe
    //             to a plan.
    //           </Typography>
    //         ) : (
    //           <Typography>
    //             Available customization options will automatically adapt based on the specific model
    //             you select.
    //           </Typography>
    //         )}
    //       </PanelHighlight>
    //       {userPlanData !== 'activePlan' && (
    //         <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
    //           <Button
    //             size="small"
    //             sx={{
    //               animation: 'shake 0.6s ease-in-out infinite',
    //               animationDelay: '4s',
    //               animationIterationCount: 'infinite',
    //               '@keyframes shake': {
    //                 '0%': { transform: 'translateX(0)' },
    //                 '20%': { transform: 'translateX(-3px)' },
    //                 '40%': { transform: 'translateX(3px)' },
    //                 '60%': { transform: 'translateX(-3px)' },
    //                 '80%': { transform: 'translateX(3px)' },
    //                 '100%': { transform: 'translateX(0)' },
    //               },
    //             }}
    //             onClick={() => {
    //               router.push('/subscription');
    //               setIsOpen(false);
    //               localStorage.removeItem('tourStep');
    //               localStorage.removeItem('tourInitialized');
    //               onChangeWelcomeTour('welcomeToT2CV2');
    //             }}
    //           >
    //             Upgrade Now
    //           </Button>
    //         </Box>
    //       )}
    //     </TourPanel>
    //   ),
    // },
  ];
};

export default getSteps;
